import type {Placement, PositioningStrategy} from '@popperjs/core';
import type {ReactNode} from 'react';
import React, {createContext, useContext} from 'react';

import type {ModifierInput} from '../../helpers/popper/popperCompat';

/**
 * Subset of react-popper `Popper` props consumers may override per-Menu. Modifiers
 * accept the legacy v1 object form (translated internally) or a v2 array.
 */
export interface MenuPopperProps {
  modifiers?: ModifierInput;
  placement?: Placement;
  strategy?: PositioningStrategy;
}

interface MenuContextValue {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
  closeOnItemClick: boolean;
  position: Placement;
  positionFixed: boolean;
  popperProps?: MenuPopperProps;
}

const MenuContext = createContext<MenuContextValue | null>(null);

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenu must be used within a Menu component');
  }
  return context;
};

export const useMenuSafe = () => {
  return useContext(MenuContext);
};

interface MenuProviderProps {
  children: ReactNode;
  value: MenuContextValue;
}

export const MenuProvider = ({children, value}: MenuProviderProps) => (
  <MenuContext.Provider value={value}>{children}</MenuContext.Provider>
);
